import * as React from 'react';
import type { ThemedToken } from 'shiki';
import { highlightCode, normalizeCodeSnippet } from '@/lib/code-highlighting';
import styles from './HighlightedCodeBlock.module.scss';

interface HighlightedCodeBlockProps extends React.HTMLAttributes<HTMLPreElement> {
  code: string;
  language?: string;
  lazy?: boolean;
  showLineNumbers?: boolean;
}

interface HighlightState {
  status: 'idle' | 'ready' | 'error';
  cacheKey: string;
  tokens: ThemedToken[][] | null;
}

const OBSERVER_ROOT_MARGIN = '240px';

function useNearViewport<T extends Element>(enabled: boolean) {
  const ref = React.useRef<T | null>(null);
  const [isNearViewport, setIsNearViewport] = React.useState(!enabled);

  React.useEffect(() => {
    if (!enabled) {
      setIsNearViewport(true);
      return;
    }

    const node = ref.current;
    if (!node) return;

    if (typeof IntersectionObserver === 'undefined') {
      setIsNearViewport(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting) return;
        setIsNearViewport(true);
        observer.disconnect();
      },
      { rootMargin: OBSERVER_ROOT_MARGIN },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [enabled]);

  return { isNearViewport, ref };
}

function renderToken(token: ThemedToken, key: string) {
  const fontStyle = token.fontStyle ?? 0;
  const className = [
    styles.token,
    fontStyle > 0 && (fontStyle & 1) !== 0 ? styles.italic : '',
    fontStyle > 0 && (fontStyle & 2) !== 0 ? styles.bold : '',
    fontStyle > 0 && (fontStyle & 4) !== 0 ? styles.underline : '',
    fontStyle > 0 && (fontStyle & 8) !== 0 ? styles.strikethrough : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span
      key={key}
      className={className}
      style={token.color ? { color: token.color } : undefined}
    >
      {token.content}
    </span>
  );
}

export function HighlightedCodeBlock({
  code,
  language,
  lazy = true,
  showLineNumbers = false,
  className,
  ...htmlProps
}: HighlightedCodeBlockProps) {
  const normalizedCode = React.useMemo(
    () => normalizeCodeSnippet(code),
    [code],
  );
  const cacheKey = React.useMemo(
    () => `${language ?? 'tsx'}::${normalizedCode}`,
    [language, normalizedCode],
  );
  const { isNearViewport, ref } = useNearViewport<HTMLPreElement>(lazy);
  const [highlightState, setHighlightState] = React.useState<HighlightState>({
    status: 'idle',
    cacheKey,
    tokens: null,
  });

  React.useEffect(() => {
    setHighlightState((previousState) => {
      if (previousState.cacheKey === cacheKey) {
        return previousState;
      }

      return {
        status: 'idle',
        cacheKey,
        tokens: null,
      };
    });
  }, [cacheKey]);

  React.useEffect(() => {
    if (lazy && !isNearViewport) return;

    let cancelled = false;

    highlightCode(normalizedCode, language)
      .then((result) => {
        if (cancelled) return;

        React.startTransition(() => {
          setHighlightState({
            status: 'ready',
            cacheKey,
            tokens: result.tokens,
          });
        });
      })
      .catch(() => {
        if (cancelled) return;

        React.startTransition(() => {
          setHighlightState({
            status: 'error',
            cacheKey,
            tokens: null,
          });
        });
      });

    return () => {
      cancelled = true;
    };
  }, [cacheKey, isNearViewport, language, lazy, normalizedCode]);

  const isReady =
    highlightState.status === 'ready' && highlightState.cacheKey === cacheKey;
  const preClassName = [
    styles.root,
    showLineNumbers ? styles.withLineNumbers : '',
    !isReady ? styles.pending : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <pre ref={ref} className={preClassName} {...htmlProps}>
      <code className={styles.code}>
        {isReady && highlightState.tokens
          ? highlightState.tokens.map((line, lineIndex) => (
              <span key={`${cacheKey}:${lineIndex}`} className={styles.line}>
                {line.length > 0
                  ? line.map((token, tokenIndex) =>
                      renderToken(
                        token,
                        `${cacheKey}:${lineIndex}:${tokenIndex}`,
                      ),
                    )
                  : ' '}
              </span>
            ))
          : normalizedCode.split('\n').map((line, lineIndex) => (
              <span
                key={`raw:${cacheKey}:${lineIndex}`}
                className={styles.line}
              >
                {line.length > 0 ? line : ' '}
              </span>
            ))}
      </code>
    </pre>
  );
}
